/**
 * collaboration.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Subject } from "rxjs/Rx";
import { Observable } from "rxjs/Rx";
import { AlgorithmService }
  from "../algorithms/management/algorithm.service";
import { IntakeStepService }
  from "../algorithms/management/intake-step.service";
import { Collaborator } from "../auth/identity/collaborator";
import { AlertService } from "../common/alert.service";
import { MessageReceiver } from  "../server/message-receiver";
import { MessageTag } from "../server/messages/";
import { AlgorithmUsersBroadcast } from "../server/messages/algorithms/";
import {
  ChatMessageBroadcast,
  PostChatMessageRequest
} from "../server/messages/collaboration/";
import { SaveResponse } from "../server/messages/context-management/";
// import { UserConfigurationResponse } from "../server/messages/metadata/";
import { MessagingService } from "../server/messaging.service";
import { onDisconnect } from "../support/types/decorators";
import { ChatEntry } from "./chat-entry";
import { SettingService } from "app/configuration/setting.service";
import { SoundsService } from "app/support/APIs/sounds.service";
import { ExtensionSignalRequest, ExtensionPayload }
  from "app/server/messages/collaboration/extension-signal-request";
import { ExtensionSignalBroadcast }
  from "app/server/messages/collaboration/extension-signal-broadcast";
import { AcknowledgmentResponse, Message } from "app/server/messages/general";
import { UserService } from "app/auth/identity/user.service";
import { Rule } from "app/auth/permissions/rules";

@Injectable()
export class CollaborationService implements MessageReceiver
{
  private userMap : Map<number, Collaborator> = new Map<number, Collaborator>();

  public users : Collaborator [] = [];

  public userEvents : Subject<boolean> = new Subject<boolean>();

  public chatEnabled : boolean = true;

  public chatMessages : ChatEntry[] = [];

  public chatPokes : Subject<boolean> = new Subject<boolean>();

  public get (id : number) : Collaborator
  {
    return this.userMap.get(id);
  }

  private nametag (user : Collaborator) : string
  {
    return `${user.name} [${(user.org || "unknown organization")}]`;
  }

  @onDisconnect()
  private clear () : void
  {
    this.userMap.clear();
    this.users = [];
    this.chatMessages = [];
    this.chatEnabled = true;
  }

  public constructor (
    protected messaging : MessagingService,
    protected alerts : AlertService,
    protected algorithms : AlgorithmService,
    protected intakeSteps : IntakeStepService,
    protected settings : SettingService,
    protected sounds : SoundsService,
    protected userSvc : UserService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected()
      // (error : any) => this.finished(error),
      // () => this.finished({completed: true})
    );

    // The server isn't going to tell us there are "no" users when we leave a
    // coverage form, so we wait for the algorithm service to indicate we left
    this.algorithms.runningObservable.subscribe(
      (running : any) => running ? null : this.clear());
  }

  /**
   * Initialization for when the {@link MessagingService} is ready.
   */
  public connected () : void
  {
    this.messaging.receive(MessageTag.BCST_ALGORITHM_USERS).subscribe(
      (message : AlgorithmUsersBroadcast) => this.receiveUsers(message));

    this.messaging.receive(MessageTag.RSP_SAVE).subscribe(
      (message : SaveResponse) => this.receiveSave(message));

    this.messaging.receive(MessageTag.BCST_CHAT_MESSAGE).subscribe(
      (message : ChatMessageBroadcast) => this.receiveChat(message));

    this.messaging.receive(MessageTag.BCST_EXTENSION_SIGNAL)
      .filter(isIntakeLock).subscribe(
        (msg : ExtensionSignalBroadcast<IntakeLockPayload>) =>
          this.receiveLockCommand(msg));
    this.messaging.receive(MessageTag.RSP_SUBJECT_CONTEXT).subscribe(
      () => this.clearLocks());

    this.userSvc.ready.subscribe((ready : boolean) => {
      this.cached_useClientExtensions = ready &&
        this.userSvc.self.hasRule(Rule.USE_CLIENT_EXTENSIONS);
    });
  }

  protected receiveUsers (message : AlgorithmUsersBroadcast) : void
  {
    let changes : boolean = false;
    changes = changes || this.departure(message.removedId);
    changes = changes || this.arrivals(message.users);
    this.syncMap(message.users);

    this.userEvents.next(changes);
  }

  private getSortedUniqueArray () : Collaborator[]
  {
    const usersWithRepeats : IterableIterator<Collaborator> =
      this.userMap.values();
    const uniqueUsers : Collaborator[] = Array.from(usersWithRepeats)
      .filter((u, i, a) => a.indexOf(u) === i);
    return Collaborator.sort(uniqueUsers);
  }

  protected departure (id : number | null) : boolean
  {
    if (id === null)
    {
      return false;
    }
    if (!this.userMap.has(id))
    {
      // Treat users that we don't have copies of as non-events
      return false;
    }
    const user : Collaborator = this.userMap.get(id);
    const nametag : string = this.nametag(user);
    this.alerts.userInfo(nametag + " has stopped editing this subject");

    this.chatPokes.next(true);
    this.chatMessages.push(ChatEntry.leave(user.name, user.org));
    this.unlockByUser(id);

    return true;
  }

  protected arrivals (userList : Collaborator[]) : boolean
  {
    const isInitial : boolean = (this.userMap.size === 0);
    const additions : Collaborator[] = [];

    // Add the new users to our map
    userList.forEach((user : Collaborator) => {
      if (user.userId.every((id : number) => !this.userMap.has(id)))
      {
        // The user has only been "added" if NONE of their user IDs exist in the
        // current user map.
        additions.push(user);
        if (!isInitial)
        {
          this.chatPokes.next(true);
          this.chatMessages.push(ChatEntry.join(user.name, user.org));
        }
      }
    });

    if (additions.length === 0)
    {
      return false;
    }

    // Create an appropriate notification about any userlist changes
    if (isInitial) {
      // This is our initial status of the subject so don't alert when "we" join
      if (additions.length > 1)
      {
        this.alerts.userInfo(
          "This subject has " + additions.length + " users editing it");
        return true;
      }
      return false;
    }
    else
    {
      if (additions.length > 1)
      {
        // Multiple joins (probably doesn't happen!)
        this.alerts.userInfo(
          additions.length + " other users have begun editing this subject");
      }
      else // We know additions.length is 1 because we excluded the 0 case above
      {
        // Single user join
        const user : Collaborator = additions[0];
        const nametag : string = this.nametag(user);
        this.alerts.userInfo(nametag + " has begun editing this document");
      }
    }
    if (this.soundsEnabled())
    {
      this.sounds.playSound("arrive");
    }
    if (this.myLock !== -1)
    {
      this.sendCurrentLock();
    }
    return true;
  }

  private soundsEnabled () : boolean
  {
    return this.settings.get("sounds enabled") || false;
  }

  public syncMap (userList : Collaborator[]) : void
  {
    this.userMap.clear();
    // For every user, for every ID, add the user.
    userList.forEach((user : Collaborator) => {
      user.userId.forEach((id : number) => this.userMap.set(id, user));
    });
    this.users = this.getSortedUniqueArray();
  }

  protected receiveSave (message : SaveResponse) : void
  {
    if (!this.userMap.has(message.userId))
    {
      throw new Error(
        "Received a save event from a user not in the local user list!");
    }
    const user : Collaborator = this.userMap.get(message.userId);
    const nametag : string = this.nametag(user);
    this.alerts.notify(nametag + " saved the form!");

    // indicate an external user saved the algorithm, for save cleanliness
    // determinations TODO: better change tracking?
    this.algorithms.running.lastExternalSave = new Date();
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected () : void {/* decorator generated */}

  public receiveChat (message : ChatMessageBroadcast) : void
  {
    const author : Collaborator | null = this.get(message.userId);
    if (author === undefined)
    {
      throw new Error("Received chat from unknown userid: " + message.userId);
      // author = new Collaborator(
      //   `Unknown #${message.userId}`,
      //   [message.userId],
      //   "N/A"
      // );
    }

    if (this.soundsEnabled())
    {
      this.sounds.playSound("chat");
    }
    this.chatPokes.next(true);
    this.chatMessages.push(
      new ChatEntry(message.message, author.name, author.org));
  }

  public sendChat (text : string, pushSelf : boolean = true)
    : Observable<boolean>
  {
    return this.messaging.resultOf(new PostChatMessageRequest(text)).do(
      (sent : boolean) => {
        if (sent && pushSelf) {
          this.chatMessages.push(new ChatEntry(text, "Me"));
        }
      });
  }

  private cached_useClientExtensions : boolean = false;
  private userCanSendLocks () : boolean
  {
    return this.cached_useClientExtensions;
  }

  public sendStepLock (id : number, table : string | null = null) : void
  {
    if (!this.userCanSendLocks() || this.users.length === 0)
    {
      return;
    }
    if (id === this.myLock)
    {
      console.warn(`Skipping redundant lock for id ${id}`);
    }
    else
    {
      this.messaging.send(
        new ExtensionSignalRequest(
          new IntakeLockPayload(id, table))).subscribe(
            (r : Message) => {
              if (AcknowledgmentResponse.isOk(r))
              {
                this.myLock = id;
              }
            }
          );
    }
  }

  private sendCurrentLock () : void
  {
    if (!this.userCanSendLocks() || this.users.length === 0)
    {
      return;
    }
    if (this.myLock === -1)
    {
      throw Error("No lock currently set... cannot resend update.");
    }
    this.messaging.send(
      new ExtensionSignalRequest(
        new IntakeLockPayload(this.myLock, this.myLockTable))).subscribe();
  }

  public sendUnlock () : void
  {
    if (!this.userCanSendLocks())
    {
      return;
    }
    if (this.myLock === -1)
    {
      console.warn(`Skipping redundant unlock `);
    }
    else
    {
      this.messaging.send(
        new ExtensionSignalRequest(
          new IntakeLockPayload())).subscribe(
            (r : Message) => {
              if (AcknowledgmentResponse.isOk(r))
              {
                this.myLock = -1;
              }
            }
          );
    }
  }

  private myLock : number = -1;
  private myLockTable : string | null = null;
  private locksByUser : Map<number, number> = new Map();
  private lastLockCommandByUser : Map<number, number> = new Map();
  public locks : Set<number> = new Set();
  public tableLocks : Set<string> = new Set();
  private tableLocksByFocusedStep : Map<number, string> = new Map();
  private lastLocker : Map<number | string, number> = new Map();

  private unlockByUser (userId : number) : void
  {
    const toRemove : number = this.locksByUser.get(userId);
    this.locks.delete(toRemove);
    this.locksByUser.delete(userId);
    const removedTable : string =
      this.tableLocksByFocusedStep.get(toRemove);
    if (removedTable !== undefined)
    {
      this.tableLocks.delete(removedTable);
      this.tableLocksByFocusedStep.delete(toRemove);
    }
  }

  private lockByUser (
    userId : number, stepId : number, tableName : string | null) : void
  {
    this.locksByUser.set(userId, stepId);
    this.lastLocker.set(stepId, userId);
    this.locks.add(stepId);
    if (tableName)
    {
      this.tableLocks.add(tableName);
      this.tableLocksByFocusedStep.set(stepId, tableName);
      this.lastLocker.set(tableName, userId);
    }
  }

  private receiveLockCommand (
    msg : ExtensionSignalBroadcast<IntakeLockPayload>
  ) : void
  {
    const lastCommandId : number =
      this.lastLockCommandByUser.get(msg.senderId) || -1;
    if (msg.payload.commandId > lastCommandId)
    {
        this.unlockByUser(msg.senderId);
        if (msg.payload.stepId > -1)
        {
          this.lockByUser(
            msg.senderId, msg.payload.stepId, msg.payload.stepTableName);
        }
        this.lastLockCommandByUser.set(msg.senderId, msg.payload.commandId);
    }
    else
    {
      console.warn("Ignoring out-of-order lock command:", msg);
    }
  }

  private clearLocks () : void
  {
    this.myLock = -1;
    this.myLockTable = null;
    this.locksByUser.clear();
    this.lastLockCommandByUser.clear();
    this.locks.clear();
    this.tableLocks.clear();
    this.tableLocksByFocusedStep.clear();
    this.lastLocker.clear();
  }

  public findUserIdByLock (stepId : number, table? : string)
    : Collaborator | undefined
  {
    if (this.lastLocker.has(stepId))
    {
      return this.get(this.lastLocker.get(stepId));
    }
    else if (table && this.lastLocker.has(table))
    {
      return this.get(this.lastLocker.get(table));
    }
    return undefined;
  }

}

function isIntakeLock (msg : ExtensionSignalBroadcast<ExtensionPayload>)
  : msg is ExtensionSignalBroadcast<IntakeLockPayload>
{
  return msg.payload.name === INTAKE_LOCK_NAME;
}
let lockCommandIncrementor : number = 0;
type IntakeLockPayloadName = "intake-step-lock";
const INTAKE_LOCK_NAME : IntakeLockPayloadName = "intake-step-lock";
class IntakeLockPayload implements ExtensionPayload
{
  public readonly name : IntakeLockPayloadName = INTAKE_LOCK_NAME;
  public readonly version : [number, number, number] = [0, 1, 1];
  public readonly commandId : number;
  public readonly stepTableName : string;
  constructor (
    public stepId : number = -1,
    table : string | null = null
  )
  {
    this.commandId = lockCommandIncrementor++;
    if (table)
    {
      this.stepTableName = table;
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/collaboration.service.ts